import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  InputAdornment,
  IconButton
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

const UsageLimitComponent = ({
  handleUsageLimitChange,
  handleUsageLimitDataChange,
  handleUsageLimitValueChange,
  handleClearUsageLimitValue,
  usageLimitData,
  usageLimitRequired,
  loading,
}) => {
  // Frequency options as per API requirements
  const frequencyOptions = [
    { value: "HOURS", label: "Hour" },
    { value: "DAYS", label: "Day" },
    { value: "MONTH", label: "Month" },
  ];

  // Limit on options as per API requirements
  const limitOnOptions = [
    { value: "AMOUNT", label: "Amount" },
    { value: "TRANSACTION", label: "Transaction" },
  ];

  // Prevent wheel scroll on number input
  const preventScroll = (e) => {
    e.target.blur();
  };

  return (
    <Box sx={{ maxWidth: 800, margin: "auto" }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
        Usage Limit
      </Typography>
      <p className="usage-limit-subtitle">
        Define spending thresholds and transaction volume controls for this corporate account.
      </p>
      <Box sx={{ background: "#f8f9fa", borderRadius: 2, p: 3,  }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 2 }}>
          Do you want to set a usage limit?
        </Typography>

        <RadioGroup
          value={usageLimitRequired}
          onChange={(e) => handleUsageLimitChange(e.target.value)}
          row
          sx={{ gap: 3 }}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>Yes</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  Set a usage limit for corporate
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "Yes" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
          <FormControlLabel
            value="No"
            control={<Radio sx={{ color: '#14AE78', '&.Mui-checked': { color: '#14AE78' } }} />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>No</Typography>
                <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                  No usage limit required
                </Typography>
              </Box>
            }
            sx={{
              background: usageLimitRequired === "No" ? "#e8f5e9" : "transparent",
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0
            }}
          />
        </RadioGroup>
      </Box>

      {usageLimitRequired === "Yes" && (
        <Box sx={{ background: "white", borderRadius: 2, p: 3, border: "1px solid #e0e0e0" }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#333", mb: 3 }}>
            Configure Usage Limit
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {/* What to limit */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#444", mb: 2 }}>
                What do you want to limit?
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {limitOnOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={usageLimitData.limitOn === option.value ? "contained" : "outlined"}
                    onClick={() => handleUsageLimitDataChange("limitOn", option.value)}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      px: 3,
                      py: 1,
                      ...(usageLimitData.limitOn === option.value ? {
                        background: "#14AE78",
                        color: "white",
                        borderColor: "#14AE78",
                        "&:hover": {
                          background: "#0f8c65",
                          borderColor: "#0f8c65",
                        }
                      } : {
                        borderColor: "#e0e0e0",
                        color: "#666",
                        "&:hover": {
                          borderColor: "#14AE78",
                          color: "#14AE78",
                          background: "rgba(20, 174, 120, 0.04)"
                        }
                      })
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                {usageLimitData.limitOn === "AMOUNT" && "Limit based on total amount spent"}
                {usageLimitData.limitOn === "TRANSACTION" && "Limit based on number of transactions"}
                {!usageLimitData.limitOn && "Select what you want to limit"}
              </Typography>
            </Box>

            {/* Frequency */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#444", mb: 2 }}>
                Frequency
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {frequencyOptions.map((option) => (
                  <Button
                    key={option.value}
                    variant={usageLimitData.frequency === option.value ? "contained" : "outlined"}
                    onClick={() => handleUsageLimitDataChange("frequency", option.value)}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      px: 3,
                      py: 1,
                      ...(usageLimitData.frequency === option.value ? {
                        background: "#14AE78",
                        color: "white",
                        borderColor: "#14AE78",
                        "&:hover": {
                          background: "#0f8c65",
                          borderColor: "#0f8c65",
                        }
                      } : {
                        borderColor: "#e0e0e0",
                        color: "#666",
                        "&:hover": {
                          borderColor: "#14AE78",
                          color: "#14AE78",
                          background: "rgba(20, 174, 120, 0.04)"
                        }
                      })
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                {usageLimitData.frequency === "HOURS" && "Set hourly limit for corporate usage"}
                {usageLimitData.frequency === "DAYS" && "Set daily limit for corporate usage"}
                {usageLimitData.frequency === "MONTH" && "Set monthly limit for corporate usage"}
                {!usageLimitData.frequency && "Select how often the limit resets"}
              </Typography>
            </Box>

            {/* Limit Value */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: "#444", mb: 1 }}>
                Limit Value
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#999', fontWeight: 'normal' }}>
                  {usageLimitData.value?.length || 0}/6
                </span>
              </Typography>
              <TextField
                fullWidth
                value={usageLimitData.value}
                onChange={handleUsageLimitValueChange}
                onWheel={preventScroll}
                placeholder={
                  usageLimitData.limitOn === "AMOUNT" ? "Enter amount limit" :
                    usageLimitData.limitOn === "TRANSACTION" ? "Enter transaction count" :
                      "Enter limit value"
                }
                disabled={loading || !usageLimitData.limitOn || !usageLimitData.frequency}
                type="text"
                inputProps={{
                  inputMode: usageLimitData.limitOn === "TRANSACTION" ? "numeric" : "decimal",
                  style: {
                    MozAppearance: 'textfield',
                  }
                }}
                InputProps={{
                  startAdornment: usageLimitData.limitOn === "AMOUNT" ? (
                    <InputAdornment position="start">
                      <span style={{ color: '#666', fontWeight: 500 }}>₹</span>
                    </InputAdornment>
                  ) : null,
                  endAdornment: usageLimitData.value ? (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClearUsageLimitValue}
                        edge="end"
                        size="small"
                        disabled={loading}
                        sx={{
                          color: '#999',
                          '&:hover': {
                            color: '#666',
                            backgroundColor: 'rgba(0,0,0,0.04)'
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#14AE78',
                      }
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#14AE78',
                        borderWidth: 2,
                      }
                    }
                  },
                  "& input": {
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    }
                  }
                }}
              />
              <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block" }}>
                {usageLimitData.limitOn === "AMOUNT" && usageLimitData.frequency === "HOURS" && "Maximum amount allowed per hour"}
                {usageLimitData.limitOn === "AMOUNT" && usageLimitData.frequency === "DAYS" && "Maximum amount allowed per day"}
                {usageLimitData.limitOn === "AMOUNT" && usageLimitData.frequency === "MONTH" && "Maximum amount allowed per month"}
                {usageLimitData.limitOn === "TRANSACTION" && usageLimitData.frequency === "HOURS" && "Maximum transactions allowed per hour"}
                {usageLimitData.limitOn === "TRANSACTION" && usageLimitData.frequency === "DAYS" && "Maximum transactions allowed per day"}
                {usageLimitData.limitOn === "TRANSACTION" && usageLimitData.frequency === "MONTH" && "Maximum transactions allowed per month"}
                {(!usageLimitData.limitOn || !usageLimitData.frequency) && usageLimitData.limitOn && usageLimitData.frequency && "Enter the limit value"}
                {(!usageLimitData.limitOn || !usageLimitData.frequency) && (!usageLimitData.limitOn || !usageLimitData.frequency) && "Please select limit type and frequency first"}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UsageLimitComponent;